import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { isEmailish, requestEmailLink } from "../auth/emailLink";
import { parseRespondPath } from "../auth/questionnaireReturnTo";
import { tenantPath } from "../config";

// External-learner email-link request (E1 US-1.3). The learner enters their
// address and receives a one-time sign-in link. States mirror the story's
// "Tilat": idle form, submitting, sent confirmation, invalid email, unknown
// tenant, and generic error. No session is ever established here.
//
// `rate-limited` is a state of its own (task da77004b) and is NOT the resend
// cooldown: a request inside the cooldown answers 202 and lands in `sent`,
// because the flow is deliberately silent about what it did (ADR-0013 §4). A 429
// says the opposite — the request was refused before anything happened — so it
// keeps the form, says to wait, and must not read as "check your email" nor as
// the generic error it used to fall into.
type Status =
  | "idle"
  | "submitting"
  | "sent"
  | "invalid-email"
  | "unknown-tenant"
  | "rate-limited"
  | "error";

export function EmailLinkRequestPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [params] = useSearchParams();
  // THE DESTINATION THIS REQUEST IS CARRYING (task ec54522c). An external learner reaches this page from
  // the redeem page's refusal when their questionnaire login link has stopped working, and they are still
  // on their way to that questionnaire — so the path travels with the request and the backend bakes it into
  // the link it mails, which is what makes the fresh link open the form instead of the home view. Validated
  // to the one accepted shape here and again on the server; anything else is simply absent from the body.
  const returnTo = parseRespondPath(params.get("returnTo"));
  // Back to the sign-in page OF THIS TENANT: on `/t/<slug>/login/email` a bare `/login` drops the slug,
  // and on the apex host production serves every tenant from there is then no organisation to brand or
  // sign into (task f390743e).
  const loginLink = tenantPath("/login");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isEmailish(email)) {
      setStatus("invalid-email");
      return;
    }
    setStatus("submitting");
    const outcome = await requestEmailLink(email, returnTo);
    setStatus(outcome === "sent" ? "sent" : outcome);
  }

  // Success is a terminal confirmation — no form, no hint about existence.
  if (status === "sent") {
    return (
      <AuthLayout>
        <div className="auth__head">
          <span className="auth__badge auth__badge--good" aria-hidden="true">
            ✓
          </span>
          <h1 className="auth__title">{t("emailLink.sentHeading")}</h1>
          <p className="auth__text" role="status">
            {t("emailLink.sentBody")}
          </p>
        </div>
        <div className="auth__actions">
          <Link className="btn btn--block" to={loginLink}>
            {t("emailLink.backToLogin")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const invalid = status === "invalid-email";

  return (
    <AuthLayout>
      <div className="auth__head">
        <h1 className="auth__title">{t("emailLink.heading")}</h1>
        <p className="auth__subtitle">{t("emailLink.description")}</p>
      </div>

      {status === "unknown-tenant" && (
        <p role="alert" className="banner banner--error">
          {t("states.unknownTenant")}
        </p>
      )}
      {status === "rate-limited" && (
        <p role="alert" className="banner banner--warn" data-testid="email-link-rate-limited">
          {t("states.tooManyRequests")}
        </p>
      )}
      {status === "error" && (
        <p role="alert" className="banner banner--error">
          {t("states.genericError")}
        </p>
      )}

      <form className="form" onSubmit={onSubmit} noValidate>
        <label htmlFor="email">{t("emailLink.emailLabel")}</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={invalid}
          aria-describedby={invalid ? "email-error" : undefined}
        />
        {invalid && (
          <p id="email-error" role="alert" className="field-error">
            {t("emailLink.invalidEmail")}
          </p>
        )}
        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={status === "submitting"}
        >
          {status === "submitting" ? t("emailLink.sending") : t("emailLink.submit")}
        </button>
      </form>

      <Link className="auth__backlink" to={loginLink}>
        {t("emailLink.backToLogin")}
      </Link>
    </AuthLayout>
  );
}
